import { getSocialTitle } from '@/components/SourceCards/common';
import { useBlobOperations } from '@/components/SourceCards/hooks/useBlobOperations';
import { getTeritiaryTitle } from '@/components/SourceCards/Links/Links';
import { getSnipData } from '@/features/Notes/NoteSelector/getSnipDetails';
import type { FeedItemType } from '@/types';
import { formatResult } from '@/utils/formatResult';
import { getAppTitle } from '@/utils/getAppTitle';
import { getChatHeader } from '@/utils/getChatHeader';
import { getTitle } from '@/utils/public-source-utils/getTitle';

export const useFeedItemTitle = (result: FeedItemType) => {
  const formattedResult = formatCategoryResult(result);

  const { getFileName } = useBlobOperations(formattedResult);

  if (['needlnote', 'needl_notebooks'].includes(result.source)) {
    return result.note_title || '';
  }

  switch (result.category) {
    case 'chats':
      return getChatHeader(result);

    case 'documents':
    case 'clipped_reports':
    case 'drives':
      return handleDriveOrDocumentCategory(getFileName);

    case 'media':
      return handleMediaCategory(result);

    case 'notes':
      return result.note_title || '';

    case 'gmail':
      return result.subject || '(no subject)';

    case 'bse_annual_reports':
    case 'bse_conf_calls':
    case 'earnings_conf_calls':
    case 'bse_filings':
    case 'bse_sdd_sast':
    case 'bse_presentations':
    case 'creditratings':
      return handleCategoryWithCompanyName(formattedResult);

    case 'nzx_filings':
      return `${result.company_name} - ${result.headline}`;

    case 'links':
    case 'forums':
      return getTitle(result) || getTeritiaryTitle(result);

    case 'news':
    case 'blogs':
      return handleNewsOrBlogCategory(result, formattedResult);

    case 'regulators':
      return formattedResult.company_name ?? getTitle(formattedResult);

    case 'socialnetwork':
    case 'reddit':
      return getSocialTitle(result)?.title || '';

    case 'comments':
      return getSnipData(formattedResult).title;

    case 'webclips':
      return (
        result.original_filename?.[0] ||
        getAppTitle(formattedResult.source) ||
        ''
      );

    default:
      return '';
  }
};

const formatCategoryResult = (result: FeedItemType): FeedItemType => {
  switch (result.category) {
    case 'drives':
    case 'bse_annual_reports':
    case 'bse_conf_calls':
    case 'earnings_conf_calls':
    case 'bse_filings':
    case 'bse_sdd_sast':
    case 'bse_presentations':
    case 'creditratings':
    case 'news':
    case 'blogs':
    case 'regulators':
    case 'comments':
    case 'webclips':
      return formatResult(result, result.category) as FeedItemType;
    default:
      return result;
  }
};

const handleDriveOrDocumentCategory = (
  getFileName: () => [string, string, string | undefined]
) => {
  const [_, filename, extension] = getFileName();
  return `${filename}.${extension}`;
};

const handleMediaCategory = (result: FeedItemType) => {
  const [newFileName, newExtension] = Array.isArray(result.original_filename)
    ? [
        result.original_filename[0].slice(
          0,
          result.original_filename[0].lastIndexOf('.')
        ),
        result.mime_type?.toLowerCase() ?? '',
      ]
    : ['', result.mime_type?.toLowerCase() ?? ''];
  return `${newFileName}.${newExtension}`;
};

const handleCategoryWithCompanyName = (formattedResult: FeedItemType) => {
  return formattedResult.company_name || getTitle(formattedResult);
};

const handleNewsOrBlogCategory = (
  result: FeedItemType,
  formattedResult: FeedItemType
) => {
  const link = result.object_location || result.link_to_report;
  const mime = result.mime_type?.toLowerCase() ?? '';
  if (mime === 'pdf' || link?.includes('pdf')) {
    return formattedResult.company_name ?? getTitle(formattedResult);
  }
  return getTitle(result) || getTeritiaryTitle(result);
};
